import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { cloneDeep, isEmpty } from 'lodash';
import { useDPPState } from '../Context/index';
import ConfigModal from './ControlConfig/ConfigModal';
import '../DPP.css';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';
import * as bomLibAction from '../../BomLibrary/store/BomLibrary.action';
import * as action from '../Store/DPP.action';
import SectionControl from './SectionControl';
import DPPContainer from './DPPContainer';
import BasicLibraryDetailComponent from './BasicLibraryDetail';
import { createOptions, checkImageExistInCanvasData } from '../../../../Components/helper';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import DragDropSort from '../../../../Components/DragDropSort/DragDropSort';

function AddEdit() {
  const reducer = useSelector((reducerstate) => reducerstate);
  const { BomLibraryDetails, UserDetails, DPPReducer, SystemMastersDetails, Users } = reducer;
  const { dppLibraryDetail } = DPPReducer;
  const { brandList, pcTypeList, productIdentifierList, subBrandAccess, multiSubBrandList } = SystemMastersDetails;
  const { ReferenceSupplier } = BomLibraryDetails;

  const { userInfo } = UserDetails;
  const { logInRights } = Users;
  const { dppState, setDppState, toggleEditPopup } = useDPPState();
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('common');
  const { dppLibId } = useParams();
  const { editPopup, editpopupData, editPopupItemIndex, controlPanelOpen, editCustomSectionIndex } = dppState;
  const dppRef = useRef(null);
  const dppCanvasRef = useRef(null);

  const { CheckActionPermission } = useComponent();
  const DPPAddEditAccess = CheckActionPermission(logInRights, 'dpp', 'all', 'addEdit');
  const [lengthEnabled, setLengthEnabled] = useState(true);
  const [isSubBrandAccess, setIsSubBrandAccess] = useState(false);
  const [subBrandOptions, setSubBrandOptions] = useState([]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('loggedInUserInfo'));
    if (userData?.lengthVisible !== null) {
      setLengthEnabled(userData?.lengthVisible);
    }
  }, []);

  useEffect(() => {
    if (subBrandAccess) {
      setIsSubBrandAccess(subBrandAccess);
    }
  }, [subBrandAccess]);

  useEffect(() => {
    return () => {
      setDppState((oldData) => ({
        ...oldData,
        editPopup: false,
        editpopupData: null,
        editPopupItemIndex: -1,
        editCustomSectionIndex: -1,
      }));
    };
  }, []);

  const [supplierOptions, setSupplierOptions] = useState([]);
  const [state, setState] = useState({
    isEditModeOn: false,
    pageLoading: false,
    errorFields: [],
    DPPLibrartData: {
      canvasData: [],
    },
    isRearrange: false,
  });
  const { isEditModeOn, pageLoading, DPPLibrartData, errorFields } = state;
  const [libraryBasicData, setLibraryBasicData] = useState({
    dppName: '',
    productTypeId: null,
    brandId: null,
    supplierId: null,
    isActive: true,
    isDeleted: false,
    colorwayToggel: true,
    sizeLengthToggel: true,
    subBrandId: null,
  });

  useEffect(() => {
    const fetchIdentifiers = async () => {
      await dispatch(systemMasterAction.fetchProductIdentifier(t));
      await dispatch(systemMasterAction.getSubBrandAccess());
    };
    fetchIdentifiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (multiSubBrandList?.length > 0) {
      setSubBrandOptions(createOptions(multiSubBrandList, 'name', '_id'));
    }
  }, [multiSubBrandList]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        dispatch(
          systemMasterAction.getPCTypesDetails({
            loggedInUserId: userInfo?.userId,
            isActive: true,
            tier: 'Tier1',
          }),
        ),

        dispatch(
          systemMasterAction.getBrandDetails({
            loggedInUserId: userInfo?.userId,
            isActive: 1,
          }),
        ),
        dispatch(bomLibAction.fetchBomReferenceSupplier()),
      ]);
    };

    init();
    return () => {
      dispatch(action.unmountDPPLibrary());
      setState((prevState) => ({
        ...prevState,
        isEditModeOn: false,
        pageLoading: false,
        errorFields: [],
        DPPLibrartData: {
          canvasData: [],
        },
      }));
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchDPPLibraryData = async () => {
      if (dppLibId) {
        setState((prevState) => ({
          ...prevState,
          isEditModeOn: true,
          pageLoading: true,
        }));
        await dispatch(action.getDPPLibData(dppLibId));
        setState((prevState) => ({
          ...prevState,
          pageLoading: false,
        }));
      }
    };
    fetchDPPLibraryData();
  }, [dppLibId]);

  useEffect(() => {
    if (dppLibraryDetail) {
      setState((prevState) => ({
        ...prevState,
        DPPLibrartData: dppLibraryDetail,
      }));
      setLibraryBasicData((prevState) => ({
        ...prevState,
        brandId: dppLibraryDetail?.brandId,
        productTypeId: dppLibraryDetail?.productTypeId,
        supplierId: dppLibraryDetail?.supplierId,
        dppName: dppLibraryDetail?.dppName,
        colorwayToggel: dppLibraryDetail?.colorwayToggel !== undefined ? dppLibraryDetail?.colorwayToggel : true,
        sizeLengthToggel: dppLibraryDetail?.sizeLengthToggel !== undefined ? dppLibraryDetail?.sizeLengthToggel : true,
        subBrandId: dppLibraryDetail?.subBrandId ? dppLibraryDetail?.subBrandId?.map((item) => ({ label: item.name, value: item._id })) : [],
      }));
    }
  }, [dppLibraryDetail]);

  useEffect(() => {
    if (libraryBasicData?.productTypeId) {
      const ids = libraryBasicData.productTypeId?.map((ele) => ele.value);
      const list = ReferenceSupplier?.filter((element) => {
        // check if element.supplierType includes any of the ids
        return ids?.some((id) => element?.supplierType?.includes(id));
      });

      setSupplierOptions(createOptions(list, 'name', '_id'));
    }
  }, [libraryBasicData?.productTypeId, ReferenceSupplier]);

  const brandsOptions = brandList?.map((ele) => ({
    label: ele.name,
    value: ele._id,
  }));
  const productTypeOptions = pcTypeList?.map((ele) => ({
    label: ele.name,
    value: ele._id,
  }));

  const saveConfigData = (obj, i, customSecIndex) => {
    if (obj?.key !== 'care_user_instruction') {
      setState((prevState) => ({ ...prevState, pageLoading: true }));
    }

    const DPPLibraryDataClone = cloneDeep(DPPLibrartData);
    const cloneObj = cloneDeep(obj);

    if (customSecIndex >= 0) {
      DPPLibraryDataClone.canvasData[i].controls[customSecIndex] = cloneObj;
    } else {
      DPPLibraryDataClone.canvasData[i] = cloneObj;
    }

    setState((prevState) => ({
      ...prevState,
      DPPLibrartData: DPPLibraryDataClone,
      pageLoading: false,
    }));

    setDppState((oldData) => ({
      ...oldData,
      editPopup: false,
      editpopupData: null,
      editPopupItemIndex: -1,
      editCustomSectionIndex: -1,
    }));
  };

  const handleExpand = () => {
    setDppState((prevState) => ({
      ...prevState,
      controlPanelOpen: !controlPanelOpen,
    }));
  };

  const isValidForm = (formData) => {
    const errorFields = [];
    const regex = /^[a-zA-Z0-9() \-]*$/;
    if (formData?.dppName === null || isEmpty(formData?.dppName) || !regex.test(formData?.dppName)) {
      errorFields.push('dppName');
    }
    if (formData?.productTypeId === null || isEmpty(formData?.productTypeId)) {
      errorFields.push('productTypeId');
    }
    if (formData?.brandId === null || isEmpty(formData?.brandId)) {
      errorFields.push('brandId');
    }
    if (formData?.supplierId === null || isEmpty(formData?.supplierId)) {
      errorFields.push('supplierId');
    }

    setState((prevState) => ({
      ...prevState,
      errorFields,
    }));

    return errorFields.length <= 0;
  };

  const uploadImageTos3 = async (data) => {
    try {
      return await Promise.all(
        data.map(async (item) => {
          const res = await dispatch(
            action.uploadDPPImage({
              documentName: item.documentName,
              document: item.document,
              isToReplace: item.isToReplace,
            }),
          );
          // Assuming the response data contains the value to update
          return {
            ...item,
            s3Path: res.data, // Update with the correct key from the response
          };
        }),
      );
    } catch (error) {
      console.error('Error occurred while calling the API:', error);
    }
  };

  const setValuesToObject = (vl, s3ImageToUpload, i) => {
    switch (vl?.key) {
      case 'testing_control': {
        if (vl?.testGroup?.length > 0) {
          const filterTestImagePath = s3ImageToUpload?.filter((vls) => vls.canvasIndex === i);
          vl.testGroup = vl.testGroup.map((tes, tesi) => {
            const findImagePath = filterTestImagePath?.find((vls) => vls.testGroupIndex === tesi);
            if (findImagePath) {
              tes[findImagePath.keyName] = findImagePath?.s3Path;
            }
            return tes;
          });
        }
        break;
      }
      case 'product_journey': {
        const findProductJourneyImagePaths = s3ImageToUpload?.filter((vls) => vls.canvasIndex === i);
        const findTranportIconPaths = s3ImageToUpload?.filter((vls) => vls.keyName === 'transportIcon');
        if (findProductJourneyImagePaths?.length > 0) {
          findProductJourneyImagePaths?.forEach((imagePath) => {
            if (vl?.tier?.length > 0) {
              vl?.tier?.forEach((tierData, vlTierIndex) => {
                if (imagePath.tierIndex === vlTierIndex) {
                  tierData[imagePath.keyName] = imagePath.s3Path;
                }
              });
            }
          });
        }
        if (findTranportIconPaths?.length > 0) {
          findTranportIconPaths?.forEach((imagePath) => {
            if (imagePath.canvasIndex === i) {
              vl.transportIcon = imagePath.s3Path;
            }
          });
        }
        break;
      }
      case 'heading': {
        const findHeadingIconPath = s3ImageToUpload?.filter((vls) => vls.keyName === 'iconPath');
        if (findHeadingIconPath?.length > 0) {
          findHeadingIconPath?.forEach((imagePath) => {
            if (imagePath.canvasIndex === i) {
              vl.iconPath = imagePath.s3Path;
            }
          });
        }
        break;
      }
      case 'company_stamp': {
        const findHeadingIconPath = s3ImageToUpload?.filter((vls) => vls.keyName === 'iconCompanyPath');
        if (findHeadingIconPath?.length > 0) {
          findHeadingIconPath?.forEach((imagePath) => {
            if (imagePath.canvasIndex === i) {
              vl.iconCompanyPath = imagePath.s3Path;
            }
          });
        }
        break;
      }
      case 'url_link': {
        const findURLImagePath = s3ImageToUpload?.filter((vls) => vls.keyName === 'url_image');
        if (findURLImagePath?.length > 0) {
          findURLImagePath?.forEach((imagePath) => {
            if (imagePath.canvasIndex === i) {
              vl.metaData.image = imagePath.s3Path;
            }
          });
        }
        break;
      }
      case 'navigation': {
        if (vl?.anchorMenu?.length > 0) {
          const filterTestImagePath = s3ImageToUpload?.filter((vls) => vls.canvasIndex === i);
          vl.anchorMenu = vl.anchorMenu.map((tes, tesi) => {
            const findImagePath = filterTestImagePath?.find((vls) => vls.testGroupIndex === tesi);
            if (findImagePath) {
              tes[findImagePath.keyName] = findImagePath?.s3Path;
            }
            return tes;
          });
        }
        break;
      }
      case 'care_user_instruction': {
        if (vl?.careLabelDescriptionArr?.length > 0) {
          const filterTestImagePath = s3ImageToUpload?.filter((vls) => vls.canvasIndex === i);
          vl.careLabelDescriptionArr = vl.careLabelDescriptionArr.map((tes, tesi) => {
            const findImagePath = filterTestImagePath?.find((vls) => vls.testGroupIndex === tesi);
            if (findImagePath) {
              tes[findImagePath.keyName] = findImagePath?.s3Path;
            }
            return tes;
          });
        }
        break;
      }
      default:
        break;
    }
    return vl;
  };

  const addEditModule = async () => {
    const moduleData = cloneDeep({ ...DPPLibrartData, ...libraryBasicData });

    if (isValidForm(moduleData)) {
      let canvasData = dppRef.current ? dppRef.current.getActivePageData() : [];
      // check the image exist in canvasDaat ,then upload to s3 and then call the upload api
      const { s3ImageToUpload, s3SectionImageToUpload } = await checkImageExistInCanvasData(canvasData, dppLibraryDetail?.canvasData, isEditModeOn);
      if (s3ImageToUpload?.length || s3SectionImageToUpload?.length) {
        const [uploadedS3Image, uploadedS3SectionImage] = await Promise.all([
          s3ImageToUpload?.length ? uploadImageTos3(s3ImageToUpload) : s3ImageToUpload,
          s3SectionImageToUpload?.length ? uploadImageTos3(s3SectionImageToUpload) : s3SectionImageToUpload,
        ]);
        canvasData = canvasData?.map((vl, i) => {
          vl = setValuesToObject(vl, uploadedS3Image, i);
          if (vl?.controls?.length > 0 && vl.key === 'custom_section' && uploadedS3SectionImage?.length > 0) {
            vl.controls = vl?.controls?.map((vls, ind) => {
              vls = setValuesToObject(vls, uploadedS3SectionImage, ind);
              return vls;
            });
          }
          return vl;
        });
      }

      moduleData.dppName = libraryBasicData?.dppName;
      moduleData.productTypeId = libraryBasicData?.productTypeId?.map((item) => item.value);
      moduleData.brandId = libraryBasicData?.brandId?.map((item) => item.value);
      moduleData.supplierId = libraryBasicData?.supplierId?.map((item) => item.value);
      moduleData.colorwayToggel = libraryBasicData?.colorwayToggel;
      moduleData.sizeLengthToggel = libraryBasicData?.sizeLengthToggel;
      const manipulatedCanvasDaata = await functionToManipulateRequestPayload(canvasData);
      moduleData.canvasData = manipulatedCanvasDaata;
      if (libraryBasicData?.subBrandId?.length > 0) {
        moduleData.subBrandId = libraryBasicData?.subBrandId?.map((item) => item.value);
      }
      if (isEditModeOn && !pageLoading) {
        setState((prevState) => ({
          ...prevState,
          pageLoading: true,
        }));
        const res = await dispatch(action.updateDPPLibrary(moduleData, dppLibId, t));
        if (res?.code === 200) {
          await dispatch(action.getDPPLibData(dppLibId));
        }
        setState((prevState) => ({
          ...prevState,
          pageLoading: false,
        }));
      } else if (!pageLoading) {
        setState((prevState) => ({
          ...prevState,
          pageLoading: true,
        }));
        const res = await dispatch(action.addDPPLibrary(moduleData, t));
        if (res?.code === 200) {
          history.push(`/libraries/dpp/edit/${res?.data?._id}`);
        }
        setState((prevState) => ({
          ...prevState,
          pageLoading: false,
        }));
      }
    }
  };

  const handleBasicDetail = useCallback((key, value) => {
    setState((prevState) => ({
      ...prevState,
      errorFields: prevState?.errorFields?.filter((data) => data !== key),
    }));
    setLibraryBasicData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  }, []);

  useEffect(() => {
    if (isEmpty(libraryBasicData?.brandId)) {
      setLibraryBasicData((prevState) => ({
        ...prevState,
        subBrandId: [],
      }));
      setSubBrandOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryBasicData?.brandId]);

  const functionToManipulateRequestPayload = (canvasData) => {
    canvasData?.forEach((can) => {
      if (can?.key === 'google' && can?.isAddressType === 'nearPlaces') {
        can.relatedPlacesNearMe = [];
      }
      if (can?.key === 'source') {
        if (can.moduleName === 'productIdentifier') {
          const { output } = can.relation;
          can.relation.output = output;
        }
      }
      if (can?.key === 'care_user_instruction') {
        const keysToRemove = ['fieldOptions', 'columnValues']; // List of keys to remove
        can.careLabelsArr = can.careLabelsArr
          ?.map((care) => {
            const updatedCare = {
              ...care,
              careLabel: care.careLabel?._id || null,
              fieldName: care.fieldName?._id || null,
            };

            // Dynamically remove keys
            keysToRemove.forEach((key) => {
              delete updatedCare[key];
            });

            // Only return non-empty objects
            return Object.keys(updatedCare).length > 0 ? updatedCare : null;
          })
          .filter(Boolean);
      }
      if (can?.key === 'custom_section' && can?.controls?.length > 0) {
        can?.controls?.forEach((con) => {
          if (con?.key === 'google' && con?.isAddressType === 'nearPlaces') {
            con.relatedPlacesNearMe = [];
          }
          if (con?.key === 'source') {
            if (con.moduleName === 'productIdentifier') {
              const { output } = con.relation;
              con.relation.output = output;
            }
          }
          if (con?.key === 'care_user_instruction') {
            const keysToRemove = ['fieldOptions', 'columnValues']; // List of keys to remove
            con.careLabelsArr = can.careLabelsArr
              ?.map((care) => {
                const updatedCare = {
                  ...care,
                  careLabel: care.careLabel?._id,
                  fieldName: care.fieldName?._id,
                };

                // Dynamically remove keys
                keysToRemove.forEach((key) => {
                  delete updatedCare[key];
                });

                // Only return non-empty objects
                return Object.keys(updatedCare).length > 0 ? updatedCare : null;
              })
              .filter(Boolean);
          }
        });
      }
    });
    return canvasData;
  };

  // Function to scroll to a specific element by ID within the dpp-canvas
  const scrollToElementWithinCanvas = (elementId) => {
    const canvasElement = dppCanvasRef.current;
    const targetElement = document.getElementById(elementId);
    setDppState((otherStates) => ({
      ...otherStates,
      navigateId: elementId,
    }));
    if (canvasElement && targetElement) {
      // Scroll to the target element within the canvas
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center', // Adjust based on how you want the element to align
        inline: 'nearest',
      });
    }
  };
  const updateParentCanvasArray = (arr) => {
    const DPPLibrartDataClone = cloneDeep(DPPLibrartData);
    // Update libraryAllDataClone with the modified clonedPages
    DPPLibrartDataClone.canvasData = arr;

    setState((prevState) => ({
      ...prevState,
      DPPLibrartData: DPPLibrartDataClone,
    }));
  };

  const handleShowRearrangeModal = () => {
    setState((otherState) => ({
      ...otherState,
      isRearrange: !state?.isRearrange,
    }));
  };

  const handleSaveSequence = async (_e, sequenceList) => {
    const DPPLibrartDataClone = cloneDeep(DPPLibrartData);

    DPPLibrartDataClone.canvasData = sequenceList?.sequence;
    setState((prevState) => ({
      ...prevState,
      DPPLibrartData: DPPLibrartDataClone,
    }));
    handleShowRearrangeModal();
  };
  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center">
          <h4 className="title">{t('DPP.PAGE_TITLE')}</h4>
        </div>

        <div className="search-filter care-library-filter">
          <div className="action-icons-container">
            <Link className="btn" to="/libraries/dpp">
              {isEditModeOn ? <span title={t('DPP.BACK')} className="icon-close f-20" /> : <span title={t('DPP.CANCEL')} className="icon-close f-20" />}
            </Link>
          </div>
        </div>
        <div className="floting-actions">
          <button className="btn p-0" type="button" name="Setting" title="Page Control" onClick={handleExpand} aria-label="Rearrange">
            <i className="icon-configuration f-22" />
          </button>
          <button
            className="btn p-0"
            type="button"
            name="Rearrange"
            title={t('ADD_PO_TEMPLATE_PROVIDER.REARRANGE')}
            onClick={handleShowRearrangeModal}
            aria-label="button"
          >
            <i className="icon-Rearrange  f-24" />
          </button>
          <button type="button" onClick={() => addEditModule()} className="btn p-0">
            {isEditModeOn ? <i title={t('DPP.UPDATE')} className="icon-Save-1 f-28" /> : <i title={t('DPP.SAVE')} className="icon-Save-1 f-28" />}
          </button>
        </div>
      </div>

      <Container className="p-0 position-relative flex-fill overflow-hidden h-100" fluid>
        <DndProvider backend={HTML5Backend}>
          <div className="add-edit-container h-100">
            <SectionControl />
            <div className="dpp-canvas" ref={dppCanvasRef}>
              <BasicLibraryDetailComponent
                libraryData={libraryBasicData}
                errorFields={errorFields}
                handleBasicDetail={handleBasicDetail}
                brandsOptions={brandsOptions || []}
                productTypeOptions={productTypeOptions || []}
                supplierOptions={supplierOptions || []}
                lengthEnabled={lengthEnabled}
                canvasData={DPPLibrartData?.canvasData}
                isSubBrandAccess={isSubBrandAccess}
                subBrandOptions={subBrandOptions}
              />
              {pageLoading ? (
                <Loader />
              ) : (
                <DPPContainer
                  onClick={(id) => scrollToElementWithinCanvas(id)}
                  ref={dppRef}
                  canvasData={DPPLibrartData?.canvasData}
                  DPPAddEditAccess={DPPAddEditAccess}
                  updateParentCanvasArray={updateParentCanvasArray}
                  libraryBasicData={libraryBasicData}
                />
              )}
            </div>
          </div>
        </DndProvider>
        {editPopup && (
          <ConfigModal
            show={editPopup}
            toggle={toggleEditPopup}
            data={editpopupData}
            dataIndex={editPopupItemIndex}
            setCustomModuleState={setDppState}
            submit={saveConfigData}
            customSectionIndex={editCustomSectionIndex}
            isFromCustomSection={editCustomSectionIndex >= 0}
            allCustomSection={DPPLibrartData?.canvasData?.filter((vl) => vl.key === 'custom_section')}
            customSectionIndexs={DPPLibrartData?.canvasData?.filter((vl) => vl.key === 'custom_section')?.map((vl) => vl.uniqueId)}
            libraryBasicData={libraryBasicData}
          />
        )}
        {state?.isRearrange && (
          <DragDropSort
            isShow={state?.isRearrange}
            isToggle={handleShowRearrangeModal}
            fromComp="POTemplate"
            itemList={DPPLibrartData?.canvasData}
            saveSequence={handleSaveSequence}
            productIdentifierList={productIdentifierList}
          />
        )}
      </Container>
    </>
  );
}

export default AddEdit;
